import { LanguageProvider } from '@/components/LanguageProvider'
import { Layout } from '@/components/Layout'
import { SessionExpiredModal } from '@/components/SessionExpiredModal'
import { Toaster } from '@/components/ui/sonner'
import { ClubDetailPage } from '@/pages/ClubDetailPage'
import { ClubsPage } from '@/pages/ClubsPage'
import { LeaderboardPage } from '@/pages/LeaderboardPage'
import { LoginPage } from '@/pages/LoginPage'
import { PlayersPage } from '@/pages/PlayersPage'
import { RulesTestPage } from '@/pages/RulesTestPage'
import { SeriesDetailPage } from '@/pages/SeriesDetailPage'
import { SeriesListPage } from '@/pages/SeriesListPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { UserManualPage } from '@/pages/UserManualPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { useAppStore } from '@/store'
import { useAuthStore } from '@/store/auth'
import { initTheme, getDailyRandomBackground, applyBackground } from '@/styles/themes'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Route, BrowserRouter as Router, Routes, Navigate } from 'react-router-dom'
import './i18n'

// Initialize theme on app load (before render)
initTheme()

// Component to redirect root to appropriate page
function RootRedirect() {
  const { user } = useAuthStore()
  
  // If user is authenticated, redirect to series
  if (user) {
    return <Navigate to="/series" replace />
  }
  
  // If unauthenticated, redirect to manual
  return <Navigate to="/manual" replace />
}

function App() {
  const { language, theme, background } = useAppStore()
  const { user } = useAuthStore()
  const { i18n } = useTranslation()

  // Sync language changes
  useEffect(() => {
    if (language && i18n.language !== language) {
      i18n.changeLanguage(language)
    }
  }, [language, i18n])

  // Sync theme and background changes (in case store updates from another tab)
  useEffect(() => {
    if (theme || background) {
      initTheme()
    }
  }, [theme, background])

  // Apply daily random background for non-authenticated users
  useEffect(() => {
    if (!user && background === 'none') {
      const dailyBg = getDailyRandomBackground()
      applyBackground(dailyBg).catch(_err => {
        // Silent fail - background is non-critical
      })
    }
  }, [user, background])

  return (
    <LanguageProvider>
      <Router>
        <Layout>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/series" element={<SeriesListPage />} />
            <Route path="/clubs" element={<ClubsPage />} />
            <Route path="/clubs/:id" element={<ClubDetailPage />} />
            <Route path="/series/:id" element={<SeriesDetailPage />} />
            <Route path="/series/:id/leaderboard" element={<LeaderboardPage />} />
            <Route path="/players" element={<PlayersPage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/manual" element={<UserManualPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/login" element={<LoginPage />} />
            <Route path="/rules-test" element={<RulesTestPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Layout>
        <SessionExpiredModal />
        <Toaster position="top-right" />
      </Router>
    </LanguageProvider>
  )
}

export default App